import React, { FC, ReactNode, useState } from "react";
import { CheckIcon, ChevronDownIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useClickOutside } from "@/hooks/useClickOutside";
import { Button } from "../ui/button";

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  value?: string[];
  onValueChange?: (value: string[]) => void;
  placeholder: string;
  className?: string;
  onApply?: () => void;
  containerClassName?: string;
  dropdownClassName?: string;
  footerAction?: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
    disabled?: boolean;
  };
}

const MultiSelect: FC<MultiSelectProps> = ({
  options,
  value = [],
  onValueChange,
  placeholder,
  className,
  onApply,
  containerClassName,
  dropdownClassName,
  footerAction,
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useClickOutside<HTMLDivElement>(
    () => setOpen(false),
    open,
  );

  const handleSelect = (optionValue: string) => {
    if (!onValueChange) return;

    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];

    onValueChange(newValue);
  };

  const handleApply = () => {
    setOpen(false);
    onApply?.();
  };

  return (
    <div
      className={cn(
        containerClassName,
        "relative max-w-fit text-sm text-zinc-900",
      )}
      ref={dropdownRef}
    >
      <button
        type={"button"}
        className={cn(
          "border-base-border flex min-h-10 w-fit items-center justify-between gap-2 rounded-md border bg-white px-4 py-1 text-left text-sm whitespace-nowrap text-black/45 shadow-xs transition-[color,box-shadow,border-radius] outline-none",
          "focus-visible:border-custom-outline focus-visible:ring-custom-outline/50 focus-visible:ring-[3px]",
          open && "border-custom-outline rounded-b-none border-b-transparent",
          className,
        )}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="truncate">{placeholder}</span>
        <ChevronDownIcon
          className={cn(
            "size-4 shrink-0 transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <div
          className={cn(
            "border-custom-outline hide-scrollbar absolute top-full left-0 z-50 max-h-[174px] w-full overflow-y-auto rounded-b-md border border-t-0 bg-white shadow-xs",
            dropdownClassName,
          )}
        >
          {options.map((option) => {
            const isSelected = value.includes(option.value);
            return (
              <div
                key={option.value}
                className="relative flex cursor-pointer items-center gap-2 py-1 ps-3 pe-2 capitalize hover:bg-zinc-100 focus:bg-zinc-100"
                onClick={() => handleSelect(option.value)}
              >
                <div
                  className={cn(
                    "flex size-5 min-w-5 items-center justify-center rounded border",
                    isSelected
                      ? "border-zinc-900/80 bg-zinc-900/80"
                      : "border-base-border",
                  )}
                >
                  {isSelected && <CheckIcon className="size-5 text-white" />}
                </div>
                <span className="flex-1 break-words">{option.label}</span>
              </div>
            );
          })}

          {footerAction && (
            <button
              type="button"
              className="border-base-border text-royal-glow-2 flex w-full cursor-pointer items-center gap-2 border-t px-3 py-2 text-left text-sm font-medium hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={footerAction.disabled}
              onClick={(event) => {
                event.stopPropagation();
                setOpen(false);
                footerAction.onClick();
              }}
            >
              {footerAction.icon}
              <span>{footerAction.label}</span>
            </button>
          )}

          {onApply && (
            <div className="mx-auto mt-2 mb-2 flex justify-center">
              <Button className="w-[90%]" onClick={handleApply}>
                Apply
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
